import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import styles from '@/common/containers/TasksList/styles.less';
import cx from 'classnames';
import MuiTable from '@/common/components/Table/index';
import { TASK_STATUS } from '@/common/constants/constants';
import { getTaskStatusLabel } from '@/common/constants/task-status';
import taskStyles from '@/common/styles/taskStatus.less';
import { routes } from '@/app/routing/index';
import { ReactBB } from '@/common/utils/index';
import { useFilters, useLocalizedField } from '@/common/hooks/index';
import { useTranslation } from 'react-i18next';

const buildColumns = (t) => [
  { label: t('No.'), key: 'number', sortable: false },
  t('Task'),
  { label: t('ID'), key: 'uid', sortable: true },
  { label: t('Status'), key: 'status', sortable: true },
];

const sortData = (sortBy, sortOrder, rows = []) => {
  const itemsToSort = [...rows];
  let sortedItems = [];
  let compareFn = null;
  switch (sortBy) {
    case 'uid': {
      compareFn = (list) => {
        let shapedList = list.sort((t1, t2) => {
          return t1.uid &&
            t2.uid &&
            !Number.isNaN(parseInt(t1.uid.match(/\d+/)[0])) &&
            !Number.isNaN(parseInt(t2.uid.match(/\d+/)[0]))
            ? parseInt(t1.uid.match(/\d+/)[0]) -
                parseInt(t2.uid.match(/\d+/)[0])
            : 0;
        });
        if (sortOrder === 'desc') return shapedList.reverse();
        return shapedList;
      };
      break;
    }
    case 'status':
      compareFn = (list) => {
        let shapedList = Object.values(
          list.reduce(
            (acc, item) => {
              acc[item.status].push(item);
              return acc;
            },
            {
              [TASK_STATUS.NEW]: [],
              [TASK_STATUS.ONGOING]: [],
              [TASK_STATUS.ON_HOLD]: [],
              [TASK_STATUS.COMPLETED]: [],
              [TASK_STATUS.SUSPENDED]: [],
              [TASK_STATUS.DEACTIVATED]: [],
            }
          )
        );
        if (sortOrder === 'desc') shapedList = shapedList.reverse();
        shapedList = shapedList.map((arr) => {
          return arr.sort((t1, t2) => {
            return t1.uid &&
              t2.uid &&
              !Number.isNaN(parseInt(t1.uid.match(/\d+/)[0])) &&
              !Number.isNaN(parseInt(t2.uid.match(/\d+/)[0]))
              ? parseInt(t1.uid.match(/\d+/)[0]) -
                  parseInt(t2.uid.match(/\d+/)[0])
              : 0;
          });
        });
        const flatList = shapedList.flat();
        return flatList;
      };
      break;
    default:
      compareFn = undefined;
      break;
  }
  sortedItems = ['status', 'uid'].includes(sortBy)
    ? compareFn(itemsToSort)
    : itemsToSort.sort(compareFn);
  return sortedItems;
};

const DefaultTasks = ({ list = [], module }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const getLocalizedField = useLocalizedField();

  const { sortBy, sortOrder, setSorting, sortedList } = useFilters({
    type: module,
    data: list,
    sortFunc: sortData,
  });

  const requestSort = (pSortBy) => {
    return () => setSorting(pSortBy);
  };

  const getStatusLabel = (status) => getTaskStatusLabel(t, status);

  const tableData = useMemo(() => {
    return sortedList.map((task) => {
      const title = getLocalizedField(task, 'title');
      return {
        id: task.id,
        title,
        status: task.status,
        number: task.number,
        uid: task.uid,
      };
    }).map(({ id, title, status, number, uid }) => ({
      key: id,
      data: [
        {
          value: number,
          cellProps: {
            sx: {
              width: '5%',
              fontSize: '18px',
              lineHeight: '24px',
              fontWeight: '300',
              background: '#FAF9F7',
              ...(status === 'suspended' && { color: '#9E9E9E' }),
            },
          },
        },
        {
          key: title,
          value: <span>{ReactBB(title || '-')}</span>,
          cellProps: {
            sx: {
              width: '65%',
              fontWeight: '500',
              fontSize: '18px',
              lineHeight: '24px',
              ...(status === 'suspended' && { color: '#9E9E9E' }),
            },
          },
        },
        {
          key: uid,
          value: uid,
          cellProps: {
            sx: {
              width: '10%',
            },
          },
        },
          {
            key: status,
            value: (
              <span
                className={cx(taskStyles.status, taskStyles[`status-${status}`])}
              >
                {getStatusLabel(status)}
              </span>
            ),
          cellProps: {
            sx: {
              width: '20%',
            },
          },
        },
      ],
      rowProps: {
        className: styles.status,
        onClick: () => history.push(`${routes.dashboard.taskDetails}/${id}`),
        sx: {
          cursor: 'pointer',
        },
      },
    }));
  }, [getLocalizedField, sortedList, sortBy, sortOrder]);

  return (
    <div className={styles.table}>
      <MuiTable
        column={buildColumns(t)}
        data={tableData}
        requestSort={requestSort}
        sortOrder={sortOrder}
        sortBy={sortBy}
      />
    </div>
  );
};

export default DefaultTasks;
